import { lazy, Suspense, useEffect } from 'react';
import { Toaster } from "@/components/ui/toaster"
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import ScrollToTop from './components/ScrollToTop';
import Layout from '@/components/Layout';
import AdminRoute from '@/components/AdminRoute';
import CookieConsent from '@/components/CookieConsent';
// The home page shell (hero + trust bar) stays in the main file so the first screen of the most-visited
// page paints without an extra download; its below-the-fold sections load separately (see pages/Home.jsx).
// Every OTHER page is its own file, so visitors only download the page they open.
import Home from '@/pages/Home';

const AdminLayout = lazy(() => import('@/components/admin/AdminLayout'));

// Code-split every non-home route so the homepage only ships what it needs.
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Services = lazy(() => import('@/pages/Services'));
const Pricing = lazy(() => import('@/pages/Pricing'));
const About = lazy(() => import('@/pages/About'));
const Contact = lazy(() => import('@/pages/Contact'));
const FAQ = lazy(() => import('@/pages/FAQ'));
const RequestQuote = lazy(() => import('@/pages/RequestQuote'));
const BookNow = lazy(() => import('@/pages/BookNow'));
const LandlordOffer = lazy(() => import('@/pages/LandlordOffer'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const RunSheet = lazy(() => import('@/pages/RunSheet'));
const Marketing = lazy(() => import('@/pages/Marketing'));
const BookKeeper = lazy(() => import('@/pages/BookKeeper'));
const SystemHealth = lazy(() => import('@/pages/SystemHealth'));
const ThankYou = lazy(() => import('@/pages/ThankYou'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const GdprRights = lazy(() => import('@/pages/GdprRights'));
const Testimonials = lazy(() => import('@/pages/Testimonials'));
const ServiceAreas = lazy(() => import('@/pages/ServiceAreas'));
const Blog = lazy(() => import('@/pages/Blog'));
const BlogArticle = lazy(() => import('@/pages/BlogArticle'));
const SampleReports = lazy(() => import('@/pages/SampleReports'));
const Careers = lazy(() => import('@/pages/Careers'));
const PropertyInventoryLondon = lazy(() => import('@/pages/PropertyInventoryLondon'));
const LettingAgentInventories = lazy(() => import('@/pages/LettingAgentInventories'));
const PropertyInventoryManchester = lazy(() => import('@/pages/PropertyInventoryManchester'));
const PropertyInventoryUxbridge = lazy(() => import('@/pages/PropertyInventoryUxbridge'));

// Tells index.html the current page's own content is on screen (this sits inside the same Suspense
// boundary as the pages, so it only mounts once the page's code has arrived and rendered). Optional
// downloads (tracking, SDK, pop-up) wait for this signal so they never compete with the page itself.
function PageReadySignal() {
  useEffect(() => {
    const ready = () => window.markPageReady?.();
    const frame = requestAnimationFrame(() => setTimeout(ready, 0));
    // Backup: browsers pause animation frames in background tabs, so never wait more than 1 s.
    const backup = setTimeout(ready, 1000);
    return () => { cancelAnimationFrame(frame); clearTimeout(backup); };
  }, []);
  return null;
}

const PageFallback = () => (
  <div className="pt-24 min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-navy/20 border-t-orange rounded-full animate-spin" />
  </div>
);

const AuthenticatedApp = () => {
  const { authError, navigateToLogin } = useAuth();

  // Public pages render straight away — they no longer wait for the background login check.
  // (That wait delayed the homepage hero by ~2 seconds.) Staff pages still wait: AdminRoute and
  // ProtectedRoute show their own spinner until the login check has finished.

  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      // Redirect to login automatically
      navigateToLogin();
      return null;
    }
  }

  // Render the main app
  return (
    <Suspense fallback={<PageFallback />}>
    <PageReadySignal />
    <Routes>
    <Route path="/login" element={<Login />} />
    <Route path="/register" element={<Register />} />
    <Route path="/forgot-password" element={<ForgotPassword />} />
    <Route path="/reset-password" element={<ResetPassword />} />
    <Route element={<Layout />}>
      <Route path="/" element={<Home />} />
      <Route path="/services" element={<Services />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/request-quote" element={<RequestQuote />} />
      <Route path="/book-now" element={<BookNow />} />
      <Route path="/inventory-offer" element={<LandlordOffer />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/gdpr" element={<GdprRights />} />
      <Route path="/testimonials" element={<Testimonials />} />
      <Route path="/service-areas" element={<ServiceAreas />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogArticle />} />
      <Route path="/sample-reports" element={<SampleReports />} />
      <Route path="/property-inventory-london" element={<PropertyInventoryLondon />} />
      <Route path="/letting-agent-inventories" element={<LettingAgentInventories />} />
      <Route path="/property-inventory-manchester" element={<PropertyInventoryManchester />} />
      <Route path="/property-inventory-uxbridge" element={<PropertyInventoryUxbridge />} />
      <Route path="/careers" element={<Careers />} />
      <Route element={<AdminRoute />}>
        <Route element={<AdminLayout />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/run-sheet" element={<RunSheet />} />
          <Route path="/admin/marketing" element={<Marketing />} />
          <Route path="/admin/book-keeper" element={<BookKeeper />} />
          <Route path="/admin/system-health" element={<SystemHealth />} />
        </Route>
      </Route>
      <Route path="/thank-you" element={<ThankYou />} />
    </Route>
    <Route path="*" element={<PageNotFound />} />
    </Routes>
    </Suspense>
  );
};


function App() {

  return (
    <AuthProvider>
        <Router>
          <ScrollToTop />
          <AuthenticatedApp />
          <CookieConsent />
        </Router>
        <Toaster />
    </AuthProvider>
  )
}

export default App